import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import "./PhotoBooth.css";
import "./PhotoBoothMobile.css";
import prizePng from "../assets/images/optimized/prize.png";

const PhotoBooth = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [facingMode, setFacingMode] = useState("user"); // "user" for front camera, "environment" for back camera
  const [prizePosition, setPrizePosition] = useState({ x: 80, y: 75 }); // Bottom right corner, avoiding capture button
  const cameraViewRef = useRef(null);
  const previewRef = useRef(null);
  const [timer, setTimer] = useState(0);
  const [pendingCapture, setPendingCapture] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // New state for popup flow
  const [showIntroPopup, setShowIntroPopup] = useState(true);
  const [showPledgePopup, setShowPledgePopup] = useState(false);
  const [selectedPledge, setSelectedPledge] = useState("");
  const [cameraStarted, setCameraStarted] = useState(false);

  useEffect(() => {
    if (cameraStarted) {
      startCamera();
    }
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [facingMode, cameraStarted]);

  // Ensure video plays when returning to camera view
  useEffect(() => {
    if (!capturedImage && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(console.error);
    }
  }, [capturedImage, stream]);

  // Timer countdown effect
  useEffect(() => {
    if (timer > 0) {
      const timeout = setTimeout(() => setTimer(timer - 1), 1000);
      return () => clearTimeout(timeout);
    } else if (timer === 0 && pendingCapture) {
      setPendingCapture(false);
      capturePhoto();
    }
  }, [timer, pendingCapture]);

  const startCamera = async () => {
    try {
      setIsLoading(true);
      setError("");

      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }

      const newStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      setStream(newStream);
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
      }
      setIsLoading(false);
    } catch (err) {
      console.error("Error accessing camera:", err);
      setError("Unable to access camera. Please check permissions.");
      setIsLoading(false);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current && cameraViewRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      const cameraView = cameraViewRef.current;

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Mirror the video frame horizontally
      context.save();
      context.translate(canvas.width, 0);
      context.scale(-1, 1);
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      context.restore();

      // Draw the prize overlay on the canvas
      const prizeImg = new Image();
      prizeImg.crossOrigin = "anonymous";
      prizeImg.onload = () => {
        // Calculate scaling from camera view to canvas
        const viewRect = cameraView.getBoundingClientRect();
        const scale = Math.min(
          canvas.width / viewRect.width,
          canvas.height / viewRect.height
        );

        // Set prize size (maintain aspect ratio)
        const basePrizeSize = 120; // Base size in pixels
        const prizeFinalWidth = basePrizeSize * scale;
        const prizeFinalHeight = basePrizeSize * scale;

        // Position prize to match camera view: left: 80%, bottom: 10%
        // Calculate prize position based on camera view positioning
        const prizeX = canvas.width * 0.8 - prizeFinalWidth / 2; // 80% from left, centered
        const prizeY =
          canvas.height - canvas.height * 0.1 - prizeFinalHeight * 1.5; // 10% from bottom, centered

        context.drawImage(
          prizeImg,
          prizeX,
          prizeY,
          prizeFinalWidth,
          prizeFinalHeight
        );

        // Convert canvas to blob and create URL
        canvas.toBlob(
          (blob) => {
            const imageUrl = URL.createObjectURL(blob);
            setCapturedImage(imageUrl);
          },
          "image/jpeg",
          0.9
        );
      };
      prizeImg.onerror = (error) => {
        console.error("Error loading prize image:", error);
        // Continue without prize if image fails to load
        canvas.toBlob(
          (blob) => {
            const imageUrl = URL.createObjectURL(blob);
            setCapturedImage(imageUrl);
          },
          "image/jpeg",
          0.9
        );
      };
      prizeImg.src = prizePng;
    }
  };

  const retakePhoto = async () => {
    if (capturedImage) {
      URL.revokeObjectURL(capturedImage);
      setCapturedImage(null);
    }

    // Ensure camera is working when returning to camera view
    if (stream && videoRef.current) {
      // Reassign the stream to the video element
      videoRef.current.srcObject = stream;
    } else {
      // Restart camera if stream is lost
      await startCamera();
    }
  };

  const downloadWithPreview = async () => {
    if (capturedImage && previewRef.current) {
      setIsDownloading(true);

      try {
        // Create a canvas to exactly replicate the preview layout
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Set high-resolution canvas dimensions for crisp output
        const scale = 2; // For retina/high-DPI displays
        const width = 800 * scale;
        const height = 1000 * scale;
        canvas.width = width;
        canvas.height = height;

        // Scale the context to maintain proper sizing
        ctx.scale(scale, scale);
        const actualWidth = width / scale;
        const actualHeight = height / scale;

        // Fill background with the same color as the preview (.photo-preview background)
        ctx.fillStyle = "#e8f4f8";
        ctx.fillRect(0, 0, actualWidth, actualHeight);

        // Load the captured image
        const capturedImg = new Image();
        capturedImg.crossOrigin = "anonymous";

        capturedImg.onload = () => {
          try {
            // Calculate dimensions exactly matching the preview-image-container CSS
            // .preview-image-container: height: 45vh, aspect-ratio: 3/4, max-width: 350px
            const imageContainerAspectRatio = 3 / 4; // width/height from CSS
            const maxImageContainerWidth = 350;
            const imageContainerHeight = actualHeight * 0.45; // 45vh equivalent
            const imageContainerWidth = Math.min(
              maxImageContainerWidth,
              imageContainerHeight * imageContainerAspectRatio
            );

            // Calculate actual captured image dimensions within the image container
            // This simulates object-fit: cover behavior from .captured-image CSS
            const imageAspectRatio =
              capturedImg.naturalWidth / capturedImg.naturalHeight;
            let imageWidth, imageHeight;

            if (imageAspectRatio > imageContainerAspectRatio) {
              // Image is wider than container, fit to height (crop sides)
              imageHeight = imageContainerHeight;
              imageWidth = imageContainerHeight * imageAspectRatio;
            } else {
              // Image is taller than container, fit to width (crop top/bottom)
              imageWidth = imageContainerWidth;
              imageHeight = imageContainerWidth / imageAspectRatio;
            }

            // Center the entire preview-image-download div structure
            let previewDownloadX,
              previewDownloadY,
              previewDownloadWidth,
              previewDownloadHeight;

            if (confirmed) {
              // When confirmed: preview-image-download has white background, border, padding, etc.
              // Style: backgroundColor: "white", border: "1px solid #081F2D", borderRadius: 20, padding: "16px"
              const padding = 16;
              previewDownloadWidth = imageContainerWidth + padding * 2;

              // Calculate space needed for pledge text
              const pledgeText = selectedPledge || "I run for strength";
              ctx.font = "600 18px Arial, sans-serif";
              const textLineHeight = 22;
              const estimatedTextHeight = textLineHeight + 10; // Text height + margin

              previewDownloadHeight =
                imageContainerHeight + padding * 2 + estimatedTextHeight;
              previewDownloadX = (actualWidth - previewDownloadWidth) / 2;
              previewDownloadY = (actualHeight - previewDownloadHeight) / 2;

              // Draw shadow first (boxShadow: "3.93px 5.25px 11.41px 0px #00000040")
              ctx.shadowColor = "rgba(0, 0, 0, 0.25)";
              ctx.shadowBlur = 11.41;
              ctx.shadowOffsetX = 3.93;
              ctx.shadowOffsetY = 5.25;

              // Draw the preview-image-download container background
              ctx.fillStyle = "white";
              ctx.beginPath();
              const radius = 20;
              ctx.roundRect(
                previewDownloadX,
                previewDownloadY,
                previewDownloadWidth,
                previewDownloadHeight,
                radius
              );
              ctx.fill();

              // Reset shadow and draw border
              ctx.shadowColor = "transparent";
              ctx.shadowBlur = 0;
              ctx.shadowOffsetX = 0;
              ctx.shadowOffsetY = 0;
              ctx.strokeStyle = "#081F2D";
              ctx.lineWidth = 1;
              ctx.stroke();

              // Position the image container within preview-image-download (with padding)
              const imageContainerX = previewDownloadX + padding;
              const imageContainerY = previewDownloadY + padding;

              // Center the image within the image container (object-fit: cover behavior)
              const imagePosX =
                imageContainerX + (imageContainerWidth - imageWidth) / 2;
              const imagePosY =
                imageContainerY + (imageContainerHeight - imageHeight) / 2;

              // Draw the captured image with clipping to container and border radius
              ctx.save();
              ctx.beginPath();
              ctx.roundRect(
                imageContainerX,
                imageContainerY,
                imageContainerWidth,
                imageContainerHeight,
                20
              );
              ctx.clip();
              ctx.drawImage(
                capturedImg,
                imagePosX,
                imagePosY,
                imageWidth,
                imageHeight
              );
              ctx.restore();

              // Draw the pledge text exactly as in preview
              // CSS: textAlign: "left", fontWeight: 600, fontSize: 18, padding: "0 5px"
              ctx.fillStyle = "#081F2D";
              ctx.font = "600 18px Arial, sans-serif";
              ctx.textAlign = "left";

              const textX = imageContainerX + 5; // 5px padding like preview CSS
              const textY = imageContainerY + imageContainerHeight + 20; // Below image container
              const maxTextWidth = imageContainerWidth - 10; // Account for 5px padding on each side

              // Simple text rendering (no word wrapping for now to match exactly)
              ctx.fillText(pledgeText, textX, textY);
            } else {
              // When not confirmed: preview-image-download has transparent background, no border
              // The container is just the image container without extra styling
              previewDownloadWidth = imageContainerWidth;
              previewDownloadHeight = imageContainerHeight;
              previewDownloadX = (actualWidth - previewDownloadWidth) / 2;
              previewDownloadY = (actualHeight - previewDownloadHeight) / 2;

              // Center the image within the container (object-fit: cover behavior)
              const imagePosX =
                previewDownloadX + (imageContainerWidth - imageWidth) / 2;
              const imagePosY =
                previewDownloadY + (imageContainerHeight - imageHeight) / 2;

              // Draw the captured image with border radius (.captured-image border-radius: 20px)
              ctx.save();
              ctx.beginPath();
              ctx.roundRect(
                previewDownloadX,
                previewDownloadY,
                imageContainerWidth,
                imageContainerHeight,
                20
              );
              ctx.clip();
              ctx.drawImage(
                capturedImg,
                imagePosX,
                imagePosY,
                imageWidth,
                imageHeight
              );
              ctx.restore();
            }

            // Convert canvas to blob and download
            canvas.toBlob(
              (blob) => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.download = `marathon-selfie-${Date.now()}.png`;
                link.href = url;
                link.click();

                // Clean up
                URL.revokeObjectURL(url);
                setIsDownloading(false);

                setTimeout(() => navigate("/home"), 1000);
              },
              "image/png",
              0.95
            );
          } catch (drawError) {
            console.error("Error drawing preview image:", drawError);
            // Fallback to original image if drawing fails
            const link = document.createElement("a");
            link.download = `selfie-${Date.now()}.png`;
            link.href = capturedImage;
            link.click();

            setIsDownloading(false);
            setTimeout(() => navigate("/home"), 1000);
          }
        };

        capturedImg.onerror = () => {
          console.error("Captured image failed to load");
          setIsDownloading(false);
          // Fallback to original image download
          const link = document.createElement("a");
          link.download = `selfie-${Date.now()}.png`;
          link.href = capturedImage;
          link.click();

          setTimeout(() => navigate("/home"), 1000);
        };

        capturedImg.src = capturedImage;
      } catch (error) {
        console.error("Error creating preview image:", error);
        setIsDownloading(false);
        // Fallback to original image if canvas creation fails
        const link = document.createElement("a");
        link.download = `selfie-${Date.now()}.png`;
        link.href = capturedImage;
        link.click();

        setTimeout(() => navigate("/home"), 1000);
      }
    }
  };

  const savePhoto = async () => {
    if (capturedImage && previewRef.current) {
      setIsDownloading(true);
      try {
        // Create a canvas to manually draw the final image
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Set canvas dimensions (2x scale for high quality)
        const width = 800;
        const height = 1000;
        canvas.width = width;
        canvas.height = height;

        // Fill background
        ctx.fillStyle = "#e8f4f8";
        ctx.fillRect(0, 0, width, height);

        // Load the captured image
        const capturedImg = new Image();
        capturedImg.crossOrigin = "anonymous";

        capturedImg.onload = () => {
          // Calculate image dimensions to maintain aspect ratio
          const maxImageWidth = 600;
          const maxImageHeight = 800;

          // Calculate aspect ratio of the captured image
          const imageAspectRatio =
            capturedImg.naturalWidth / capturedImg.naturalHeight;
          const frameAspectRatio = maxImageWidth / maxImageHeight;

          let imageWidth, imageHeight;

          if (imageAspectRatio > frameAspectRatio) {
            // Image is wider than frame, fit to width
            imageWidth = maxImageWidth;
            imageHeight = maxImageWidth / imageAspectRatio;
          } else {
            // Image is taller than frame, fit to height
            imageHeight = maxImageHeight;
            imageWidth = maxImageHeight * imageAspectRatio;
          }

          const imageX = (width - imageWidth) / 2;
          const imageY = 100;

          // Draw white frame background
          ctx.fillStyle = "white";
          ctx.fillRect(
            imageX - 20,
            imageY - 20,
            imageWidth + 40,
            imageHeight + 40
          );

          // Draw the captured image maintaining aspect ratio
          ctx.drawImage(capturedImg, imageX, imageY, imageWidth, imageHeight);

          const drawPledgeText = () => {
            // Draw the pledge text
            ctx.fillStyle = "#081F2D";
            ctx.font = "bold 24px Arial, sans-serif";
            ctx.textAlign = "center";

            const pledgeText = selectedPledge || "I run for strength";
            const textY = imageY + imageHeight + 80;

            // Draw text with word wrapping
            const maxWidth = imageWidth;
            const words = pledgeText.split(" ");
            let line = "";
            let lineY = textY;

            for (let i = 0; i < words.length; i++) {
              const testLine = line + words[i] + " ";
              const metrics = ctx.measureText(testLine);
              const testWidth = metrics.width;

              if (testWidth > maxWidth && i > 0) {
                ctx.fillText(line, width / 2, lineY);
                line = words[i] + " ";
                lineY += 30;
              } else {
                line = testLine;
              }
            }
            ctx.fillText(line, width / 2, lineY);

            // Convert canvas to blob and download
            canvas.toBlob(
              (blob) => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.download = `marathon-selfie-${Date.now()}.png`;
                link.href = url;
                link.click();

                // Clean up
                URL.revokeObjectURL(url);
                setIsDownloading(false);

                // Optional: Show success message or navigate back
                setTimeout(() => {
                  navigate("/home");
                }, 1000);
              },
              "image/png",
              0.9
            );
          };
          // Draw the pledge text (prize is already captured in the image)
          drawPledgeText();
        };

        capturedImg.onerror = () => {
          console.error("Captured image failed to load");
          setIsDownloading(false);
          // Fallback to original image download
          const link = document.createElement("a");
          link.download = `selfie-${Date.now()}.jpg`;
          link.href = capturedImage;
          link.click();

          setTimeout(() => {
            navigate("/home");
          }, 1000);
        };

        capturedImg.src = capturedImage;
      } catch (error) {
        console.error("Error creating final image:", error);
        setIsDownloading(false);
        // Fallback to original image if canvas creation fails
        const link = document.createElement("a");
        link.download = `selfie-${Date.now()}.jpg`;
        link.href = capturedImage;
        link.click();

        setTimeout(() => {
          navigate("/home");
        }, 1000);
      }
    }
  };

  const switchCamera = () => {
    setFacingMode((prevMode) => (prevMode === "user" ? "environment" : "user"));
  };

  // Wrap the capture button logic
  const handleStartCapture = () => {
    setTimer(3);
    setPendingCapture(true);
  };

  // Popup handlers
  const handleTakePledge = () => {
    setShowIntroPopup(false);
    setShowPledgePopup(true);
  };

  const handlePledgeSelect = (pledge) => {
    setSelectedPledge(pledge);
  };

  const handleTakeSelfie = () => {
    setShowPledgePopup(false);
    setCameraStarted(true);
    setIsLoading(true);
  };

  return (
    <div className="photobooth-container">
      <Header />
      <div className="photobooth-content">
        {/* Intro Popup */}
        {showIntroPopup && (
          <div className="popup-overlay">
            <div className="popup-content intro-popup">
              <h2>AR Photobooth</h2>
              <h3>Pledge Selfie</h3>
              <p>Take a pledge by capturing a selfie with an AR filter</p>
              <div className="popup-buttons">
                <button
                  className="popup-button primary"
                  onClick={handleTakePledge}
                >
                  TAKE A PLEDGE
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pledge Selection Popup */}
        {showPledgePopup && (
          <div className="popup-overlay">
            <div className="popup-content pledge-popup">
              <h2>Take the TCS Sydney Marathon Pledge!</h2>
              <h3>Pick Your Pledge!</h3>
              <div className="pledge-options">
                <button
                  className={`pledge-option ${
                    selectedPledge === "I run for strength" ? "selected" : ""
                  }`}
                  onClick={() => handlePledgeSelect("I run for strength")}
                >
                  I run for strength
                </button>
                <button
                  className={`pledge-option ${
                    selectedPledge === "Striding forward for a better future!"
                      ? "selected"
                      : ""
                  }`}
                  onClick={() =>
                    handlePledgeSelect("Striding forward for a better future!")
                  }
                >
                  Striding forward for a better future!
                </button>
              </div>
              <button
                className="popup-button primary take-selfie-button"
                onClick={handleTakeSelfie}
                disabled={!selectedPledge}
              >
                TAKE A SELFIE
              </button>
            </div>
          </div>
        )}

        {/* Timer Overlay */}
        {timer > 0 && cameraStarted && (
          <div className="timer-overlay">
            <div className="timer-number">{timer}</div>
          </div>
        )}

        {isLoading && cameraStarted && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Starting camera...</p>
          </div>
        )}

        {error && cameraStarted && (
          <div className="error-overlay">
            <div className="error-message">
              <i className="fas fa-exclamation-triangle"></i>
              <p>{error}</p>
              <button onClick={startCamera} className="retry-button">
                Try Again
              </button>
            </div>
          </div>
        )}

        {!capturedImage && cameraStarted ? (
          <div className="camera-view" ref={cameraViewRef}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="camera-video"
            />
            <canvas ref={canvasRef} style={{ display: "none" }} />

            <img
              src={prizePng}
              alt="Prize"
              className="fixed-prize"
              style={{
                left: `${prizePosition.x}%`,
                bottom: `10%`,
              }}
            />

            <div className="camera-controls">
              <div className="control-section left-section"></div>

              <div className="control-section center-section">
                <button
                  onClick={handleStartCapture}
                  className="capture-button"
                  disabled={isLoading || error || timer > 0}
                >
                  <div className="capture-ring">
                    <div className="capture-inner"></div>
                  </div>
                </button>
              </div>

              <div className="control-section right-section">
                <button
                  onClick={() => navigate("/home")}
                  className="control-button close-button"
                  title="Close"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {capturedImage && (
          <div className="photo-preview" ref={previewRef}>
            <div className="preview-header">
              <h2>
                {confirmed
                  ? "You Look Amazing! Here's Your Marathon Moment."
                  : "Looking Strong! Here's Your Pledge Shot."}
              </h2>
            </div>
            <div
              className="preview-image-download"
              style={{
                backgroundColor: !confirmed ? "transparent" : "white",
                border: confirmed ? "1px solid #081F2D" : "none",
                borderRadius: 20,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: confirmed ? "16px" : "0",
                marginBottom: confirmed ? 16 : 32,
                // width: "100%",
                boxShadow: confirmed
                  ? "3.93px 5.25px 11.41px 0px #00000040"
                  : "none",
              }}
            >
              <div className="preview-image-container">
                <img
                  src={capturedImage}
                  alt="Captured selfie"
                  className="captured-image"
                />
              </div>
              {confirmed && (
                <>
                  <div
                    className="final-quote responsive-quote"
                    style={{
                      textAlign: "left",
                      fontWeight: 600,
                      marginBottom: 0,
                      fontSize: 18,
                      padding: "0 5px",
                    }}
                  >
                    {selectedPledge || "I run for strength"}
                  </div>
                </>
              )}
            </div>
            <div className="preview-controls">
              {confirmed ? (
                <>
                  <button
                    className="preview-button upload-button"
                    onClick={() => navigate("/home")}
                  >
                    DONE
                  </button>
                  <button
                    className="preview-button retake-button"
                    onClick={downloadWithPreview}
                    disabled={isDownloading}
                  >
                    {isDownloading ? "PROCESSING..." : "DOWNLOAD"}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={retakePhoto}
                    className="preview-button retake-button"
                  >
                    RETAKE
                  </button>
                  <button
                    onClick={() => setConfirmed(true)}
                    className="preview-button upload-button"
                  >
                    CONFIRM
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoBooth;
